import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { AdminCampaign, getAdminCampaigns } from '../../../services/admin-service';

const AdminCampaignsPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Campanhas', path: '/admin/campaigns' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const response = await getAdminCampaigns(page * rowsPerPage, rowsPerPage);

        if (response && Array.isArray(response.items)) {
          setCampaigns(response.items);
          setTotalItems(response.total);
        } else {
          enqueueSnackbar('Falha ao carregar campanhas', { variant: 'error' });
          setCampaigns([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        enqueueSnackbar('Falha ao carregar campanhas', { variant: 'error' });
        setCampaigns([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Público Alvo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.length > 0 ? (
                  campaigns.map(campaign => (
                    <TableRow
                      key={campaign.template_id}
                      hover
                      onClick={() => navigate(`/admin/campaigns/${campaign.template_id}/resellers`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{campaign.title}</TableCell>
                      <TableCell>{campaign.description}</TableCell>
                      <TableCell>{campaign.target_audience}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Nenhuma campanha encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default AdminCampaignsPage;
